import React from 'react';
import {
  StyleSheet,
  Image,
  Document,
  Page,
  View,
  Text,
} from '@react-pdf/renderer';
import {
  capitalize,
  formatPrice,
  titleCase,
  formatWithParentheses,
} from '../../utils/helpers';
import dayjs from 'dayjs';
import {
  IMAGE_ORIENTATIONS,
  PAYMENT_TYPE,
  TRANSACTION_STATUS,
} from '../../utils/constants';
import { v4 as uuid } from 'uuid';
import fallbackLogo from '../../static/img/cityville-logo-blue.png';
import sharedReportStyles from './SharedReportStyles';

const styles = StyleSheet.create(sharedReportStyles);

const HeadingSection = ({
  title,
  headerImg,
  portraitOrLandscapeLogo,
  order,
}) => {
  const {
    location_info: {
      address_1,
      address_2,
      city,
      state,
      zipcode,
      phone_number_1,
      phone_number_2,
      email,
    },
    agencies_info,
  } = order;

  return (
    <View>
      {/*
        Logo Image src will fallback to Cityville for local development purposes so we don't need to run frontend-configuration-service (FCS) 
        for this PDF to generate. If you do run FCS locally, this will get an image from S3 based on the client_slug in the token.
        This client_slug maps to that client's FCS JSON file path and grabs the headerLogoLink value. Example: metadata-src/denver-co/pos/meta.jsonnet -> headerLogoLink
        */}
      <Image
        style={
          portraitOrLandscapeLogo === IMAGE_ORIENTATIONS.PORTRAIT
            ? styles.clientBrandPortrait
            : styles.clientBrandLandscape
        }
        src={headerImg ?? fallbackLogo}
      />
      <Text style={styles.title}>{title}</Text>
      {agencies_info[0]?.description && (
        <Text style={styles.agencyHeading}>
          {agencies_info[0]?.description}
        </Text>
      )}
      <Text>
        {address_1}
        {address_2 && `, ${address_2}`}
      </Text>
      <Text>{`${city}, ${state} ${zipcode}`}</Text>
      <Text>{phone_number_1}</Text>
      <Text>{phone_number_2}</Text>
      <Text>{email}</Text>
    </View>
  );
};

const OrderDetailsSection = ({ order, order_items, isRefund }) => {
  const {
    location,
    terminal,
    completed_at,
    id,
    subtotal,
    total_service_fees,
    total_convenience_fees,
    total,
    voided_at,
  } = order;
  const isVoided = Boolean(voided_at);
  return (
    <View>
      <Text style={styles.heading}>Order Details</Text>
      <View style={styles.lineItem}>
        <Text style={styles.semiBoldText}>Location: </Text>
        <Text>{location}</Text>
      </View>
      <View style={styles.lineItem}>
        <Text style={styles.semiBoldText}>Terminal: </Text>
        <Text>{terminal}</Text>
      </View>
      <View style={styles.lineItem}>
        <Text style={styles.semiBoldText}>Order Date: </Text>
        <Text>{dayjs(completed_at).format('MM/DD/YYYY hh:mmA')}</Text>
      </View>
      {isVoided && (
        <View style={styles.lineItem}>
          <Text style={styles.semiBoldText}>Voided Date: </Text>
          <Text>{dayjs(voided_at).format('MM/DD/YYYY hh:mmA')}</Text>
        </View>
      )}
      <View style={styles.lineItem}>
        <Text style={styles.semiBoldText}>Order ID: </Text>
        <Text>{id}</Text>
      </View>
      <View style={styles.greyContainer}>
        {order_items.map((item, idx) => {
          const isLastItem = idx === order_items.length - 1;
          const receiptVisibleAttributesEntries = Object.entries(
            item.receipt_visible_attributes,
          );
          return (
            <View
              style={styles.flexContainerRow}
              key={`${item.category}-${uuid()}`}
            >
              <View style={styles.stack}>
                <View style={styles.lineItem}>
                  <Text style={styles.semiBoldText}>{item.category}</Text>
                </View>
                {receiptVisibleAttributesEntries.map((attr, idx) => {
                  const isLastAttr =
                    idx === receiptVisibleAttributesEntries.length - 1;
                  const [key, value] = attr;
                  return (
                    <Text
                      style={{
                        marginBottom: !isLastItem && isLastAttr ? 8 : 0,
                      }}
                      key={`${item.category}-${uuid()}`}
                    >
                      {titleCase(key)}: {value}
                    </Text>
                  );
                })}
              </View>
              <View style={styles.lineItem}>
                {item.quantity > 1 && (
                  <Text>
                    {`${formatPrice(item.recorded_price)} per x ${
                      item.quantity
                    }`}
                  </Text>
                )}
                <Text
                  style={{
                    ...styles.semiBoldText,
                    textAlign: 'right',
                    width: '100px',
                  }}
                >
                  {item.is_negative_amount
                    ? formatWithParentheses(formatPrice(item.amount, isRefund))
                    : formatPrice(item.amount, isRefund)}
                </Text>
              </View>
            </View>
          );
        })}
        <View style={styles.smallDividerGrey} />
        <View style={styles.semiBoldText}>
          <View
            style={{
              ...styles.flexContainerRow,
              marginBottom:
                total_service_fees > 0 || total_convenience_fees > 0 ? 8 : 0,
            }}
          >
            <Text>Subtotal: </Text>
            <Text>{formatPrice(subtotal, isRefund)}</Text>
          </View>
          {total_service_fees > 0 && (
            <View style={styles.flexContainerRow}>
              <Text>Service Fees: </Text>
              <Text>{formatPrice(total_service_fees, isRefund)}</Text>
            </View>
          )}
          {total_convenience_fees > 0 && (
            <View style={styles.flexContainerRow}>
              <Text>Transaction Fee: </Text>
              <Text>{formatPrice(total_convenience_fees, isRefund)}</Text>
            </View>
          )}
          <View style={styles.smallDividerGrey} />
          <View style={{ ...styles.flexContainerRow, ...styles.boldText }}>
            <Text>Total: </Text>
            <Text>{formatPrice(total, isRefund)}</Text>
          </View>
        </View>
      </View>
    </View>
  );
};

const PaymentDetailsSection = ({ filteredTransactions, isRefund }) => (
  <View>
    <Text style={styles.heading}>Payment Details</Text>
    {filteredTransactions.map((t) => {
      const BankPayment = (
        <View>
          <Text>
            {`Bank account ending in ${
              t.bank_account?.account_number_last_four ?? '****'
            }`}
          </Text>
        </View>
      );
      const CardPayment = (
        <View>
          <Text>{`Card ending in ${t.credit_card?.last_four ?? '****'}`}</Text>
        </View>
      );
      const CashPayment = (
        <View>
          <Text>Cash</Text>
        </View>
      );
      const WirePayment = (
        <View>
          <Text>Wire Transfer</Text>
        </View>
      );
      const PaymentType = () => {
        switch (t.payment_type) {
          case PAYMENT_TYPE.CASH_PAYMENT:
            return CashPayment;
          case PAYMENT_TYPE.CARD_PAYMENT:
            return CardPayment;
          case PAYMENT_TYPE.CHECK_PAYMENT:
            return BankPayment;
          case PAYMENT_TYPE.WIRE_PAYMENT:
            return WirePayment;
          default:
            throw new Error(
              'Unhandled Payment Type when generating order receipt PDF',
            );
        }
      };
      return (
        <View style={{ marginBottom: 16 }} key={t.id}>
          <View style={styles.lineItem}>
            <Text style={styles.semiBoldText}>Payment ID: </Text>
            <Text>{t.gateway_payment_id}</Text>
          </View>
          {t.payer_name && (
            <View style={styles.lineItem}>
              <Text style={styles.semiBoldText}>Payer Name: </Text>
              <Text>{t.payer_name}</Text>
            </View>
          )}
          <View style={styles.lineItem}>
            <Text style={styles.semiBoldText}>Status: </Text>
            <Text>{capitalize(t.status)}</Text>
          </View>
          {t.payment_type === PAYMENT_TYPE.CARD_PAYMENT &&
            t.credit_card?.auth_code && (
              <View style={styles.lineItem}>
                <Text style={styles.semiBoldText}>Auth Code: </Text>
                <Text>{t.credit_card?.auth_code}</Text>
              </View>
            )}
          {t.wire_payment?.name && (
            <View style={styles.lineItem}>
              <Text style={styles.semiBoldText}>Name: </Text>
              <Text>{t.wire_payment.name}</Text>
            </View>
          )}
          {t.wire_payment?.effective_date && (
            <View style={styles.lineItem}>
              <Text style={styles.semiBoldText}>Effective Date: </Text>
              <Text>{t.wire_payment.effective_date}</Text>
            </View>
          )}
          {t.wire_payment?.description && (
            <View style={styles.lineItem}>
              <Text style={styles.semiBoldText}>Description: </Text>
              <Text style={{ maxWidth: '480px' }}>
                {t.wire_payment.description}
              </Text>
            </View>
          )}
          <View style={styles.greyContainer}>
            <View
              style={{
                ...styles.flexContainerRow,
                ...styles.semiBoldText,
                marginBottom:
                  t.service_fee > 0 || t.convenience_fee > 0 ? 8 : 0,
              }}
            >
              <PaymentType />
              <Text>{formatPrice(t.amount_processed, isRefund)}</Text>
            </View>
            {t.service_fee > 0 && (
              <View>
                <View
                  style={{
                    ...styles.flexContainerRow,
                    ...styles.semiBoldText,
                  }}
                >
                  <Text>Service Fees: </Text>
                  <Text>{formatPrice(t.service_fee, isRefund)}</Text>
                </View>
                <View style={styles.smallDividerGrey} />
                <View
                  style={{ ...styles.flexContainerRow, ...styles.boldText }}
                >
                  <Text style={styles.boldText}>Total: </Text>
                  <Text>
                    {formatPrice(t.amount_processed + t.service_fee, isRefund)}
                  </Text>
                </View>
              </View>
            )}
            {t.convenience_fee > 0 && (
              <View>
                <View
                  style={{
                    ...styles.flexContainerRow,
                    ...styles.semiBoldText,
                  }}
                >
                  <Text>Transaction Fee: </Text>
                  <Text>{formatPrice(t.convenience_fee, isRefund)}</Text>
                </View>
                <View style={styles.smallDividerGrey} />
                <View
                  style={{ ...styles.flexContainerRow, ...styles.boldText }}
                >
                  <Text style={styles.boldText}>Total: </Text>
                  <Text>
                    {formatPrice(
                      t.amount_processed + t.convenience_fee,
                      isRefund,
                    )}
                  </Text>
                </View>
              </View>
            )}
          </View>
        </View>
      );
    })}
  </View>
);

const FooterSection = ({ customFooterText }) => (
  <View style={{ marginTop: 'auto' }}>
    <Text>Signature</Text>
    <View style={styles.smallDividerGrey} />
    {customFooterText && (
      <Text style={{ textAlign: 'center', marginTop: 8 }}>
        {customFooterText}
      </Text>
    )}
  </View>
);

const OrderReceiptReportDocument = ({
  headerImg,
  portraitOrLandscapeLogo,
  order,
  order_items,
  transaction_details,
}) => {
  const isRefund = !!order.parent_id;
  const includedTransactions = [
    TRANSACTION_STATUS.SUCCESSFUL,
    TRANSACTION_STATUS.REFUNDED,
    TRANSACTION_STATUS.CANCELLED,
  ];
  const filteredTransactions = transaction_details.filter((t) =>
    includedTransactions.includes(t.status),
  );
  return (
    <Document>
      <Page style={styles.page}>
        <HeadingSection
          title="Receipt"
          headerImg={headerImg}
          portraitOrLandscapeLogo={portraitOrLandscapeLogo}
          order={order}
        />
        <View style={styles.largeDividerGrey} />
        <OrderDetailsSection
          order={order}
          order_items={order_items}
          isRefund={isRefund}
        />
        <View style={styles.largeDividerGrey} />
        <PaymentDetailsSection
          filteredTransactions={filteredTransactions}
          isRefund={isRefund}
        />
        <FooterSection
          customFooterText={order.agencies_info[0]?.pay_at_message ?? null}
        />
      </Page>
    </Document>
  );
};

export { OrderReceiptReportDocument };
